import React, { ChangeEvent, SyntheticEvent, FC, useCallback, useState, useMemo, RefObject } from 'react';
import { time } from 'uniqid';
import { TextInput } from '../TextInput';
import { Dropdown } from '../Dropdown';
import { Error } from '../Error';
import { checkOrDefault, getMonths, validateDob } from '../Datepicker';
import { toNumber, map } from 'lodash';
import { SUPPORTED_LOCALES } from '@trip-ux/constants';
import { dayjs } from '@trip-ux/common';

export interface DateOfBirthProps {
  labels: {
    day: string;
    month: string;
    year: string;
  };
  labelsFormat?: {
    day: string;
    month: string;
    year: string;
  };
  disabled?: boolean;
  dateFormat: string;
  defaultValue?: string;
  onChange: (e: string) => void;
  onBlur?: (e: string) => void;
  onFocus?: (e: SyntheticEvent) => void;
  className?: string;
  error?: string;
  id?: string;
  language?: SUPPORTED_LOCALES;
  name: string;
  required?: boolean;
  monthPlaceholder?: string;
  onError: (e: string) => void;
  disabledDays?: {
    before?: Date;
    after?: Date;
  };
  value: string;
  ariaDescribedby?: string;
  forwardRef?: RefObject<HTMLDivElement>;
  children?: React.ReactNode;
}

export const DateOfBirth: FC<DateOfBirthProps> = ({
  labels,
  labelsFormat = {
    day: '',
    month: '',
    year: ''
  },
  dateFormat,
  defaultValue,
  onChange,
  onBlur = () => true,
  onFocus = () => true,
  className,
  children,
  id = time('dob-'),
  language,
  error,
  monthPlaceholder,
  name,
  disabledDays = {},
  onError,
  disabled = false,
  value,
  required,
  ariaDescribedby,
  forwardRef
}) => {
  const [selectedDate, setSelectedDate] = useState(
    checkOrDefault(defaultValue, dateFormat, {
      year: null,
      month: -1,
      date: null
    })
  );
  const dropOptions = useMemo(() => {
    return map(getMonths(language), (label, idx) => ({ label, value: `${idx}` }));
  }, [language]);

  const onChangeHandler = useCallback(
    (
      e: ChangeEvent<HTMLInputElement>,
      change: {
        date?: number;
        month?: number;
        year?: number;
      }
    ) => {
      e && e.preventDefault && e.preventDefault();
      setSelectedDate(prev => ({ ...prev, ...change }));
    },
    []
  );

  const onBlurHandler = useCallback(
    e => {
      e && e.preventDefault && e.preventDefault();
      if (e.currentTarget.contains(e.relatedTarget)) {
        return;
      }
      const validationErr = validateDob(selectedDate, disabledDays.before, disabledDays.after);
      if (validationErr) {
        onBlur(validationErr);
        onError(validationErr);
      } else {
        const newDate = dayjs(new Date(selectedDate.year, selectedDate.month, selectedDate.date)).format(dateFormat);
        onBlur(newDate);
        value !== newDate && onChange(newDate);
        onError(validationErr);
      }
    },
    [dateFormat, disabledDays.after, disabledDays.before, onBlur, onChange, onError, selectedDate, value]
  );

  const onFocusHandler = useCallback(
    e => {
      e && e.preventDefault && e.preventDefault();
      onFocus(e);
    },
    [onFocus]
  );
  return (
    <div className={`d-flex flex-column dob ${className}`} ref={forwardRef} onBlur={onBlurHandler}>
      <label htmlFor={id}>{children}</label>
      <input id={id} type="hidden" name={name} />
      <div className="d-flex flex-row flex-wrap">
        <TextInput
          mask="99"
          type="tel"
          className="dob-date"
          disabled={disabled}
          showError={false}
          error={error}
          name={`${name}-date`}
          id={`${id}-date`}
          onFocus={onFocusHandler}
          required={required}
          value={`${selectedDate.date}`}
          ariaDescribedby={ariaDescribedby}
          onChange={e => onChangeHandler(e, { date: toNumber(e.target.value) })}>
          {labels.day} {labelsFormat.day && <span className="day-format">{labelsFormat.day}</span>}
        </TextInput>
        <Dropdown
          error={error}
          showError={false}
          isDisabled={disabled}
          id={`${id}-month`}
          name={`${name}-month`}
          options={dropOptions}
          onFocus={onFocusHandler}
          isRequired
          value={`${selectedDate.month}`}
          className="dob-month"
          ariaDescribedby={ariaDescribedby}
          onChange={val => onChangeHandler(null, { month: toNumber(val) })}
          placeholder={monthPlaceholder}
          language={language}>
          {labels.month} {labelsFormat.month && <span className="month-format">{labelsFormat.month}</span>}
        </Dropdown>
        <TextInput
          type="tel"
          mask="9999"
          error={error}
          disabled={disabled}
          showError={false}
          name={`${name}-year`}
          id={`${id}-year`}
          value={`${selectedDate.year}`}
          onFocus={onFocusHandler}
          required={required}
          className="dob-year"
          ariaDescribedby={ariaDescribedby}
          onChange={e => onChangeHandler(e, { year: toNumber(e.target.value) })}>
          {labels.year} {labelsFormat.year && <span className="year-format">{labelsFormat.year}</span>}
        </TextInput>
      </div>
      <Error errors={error} callout={false} id={ariaDescribedby} />
    </div>
  );
};
